import React, { createContext, useContext, useState, useEffect } from 'react';

// Accessibility context for managing font size and other accessibility features
const AccessibilityContext = createContext();

// Font size limits
const FONT_SIZE_LIMITS = {
  min: 0.8,
  max: 1.4,
  default: 1.0,
  step: 0.1
};

// Accessibility provider component
export const AccessibilityProvider = ({ children }) => {
  const [fontSize, setFontSize] = useState(FONT_SIZE_LIMITS.default);
  const [isHighContrast, setIsHighContrast] = useState(false);

  // Load saved accessibility preferences from localStorage
  useEffect(() => {
    const savedFontSize = localStorage.getItem('mlwb-font-size');
    const savedHighContrast = localStorage.getItem('mlwb-high-contrast');
    
    if (savedFontSize) {
      const size = parseFloat(savedFontSize);
      if (size >= FONT_SIZE_LIMITS.min && size <= FONT_SIZE_LIMITS.max) {
        setFontSize(size);
      }
    }
    
    if (savedHighContrast === 'true') {
      setIsHighContrast(true);
    }
  }, []);

  // Apply font size to document
  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}rem`;
  }, [fontSize]);

  // Apply high contrast mode
  useEffect(() => {
    if (isHighContrast) {
      document.documentElement.classList.add('high-contrast');
    } else {
      document.documentElement.classList.remove('high-contrast');
    }
  }, [isHighContrast]);

  // Function to increase font size
  const increaseFontSize = () => {
    const newSize = Math.min(fontSize + FONT_SIZE_LIMITS.step, FONT_SIZE_LIMITS.max);
    setFontSize(newSize);
    localStorage.setItem('mlwb-font-size', newSize.toString());
  };

  // Function to decrease font size
  const decreaseFontSize = () => {
    const newSize = Math.max(fontSize - FONT_SIZE_LIMITS.step, FONT_SIZE_LIMITS.min);
    setFontSize(newSize);
    localStorage.setItem('mlwb-font-size', newSize.toString());
  };

  // Function to reset font size
  const resetFontSize = () => {
    setFontSize(FONT_SIZE_LIMITS.default);
    localStorage.setItem('mlwb-font-size', FONT_SIZE_LIMITS.default.toString());
  };

  // Function to toggle high contrast
  const toggleHighContrast = () => {
    const newValue = !isHighContrast;
    setIsHighContrast(newValue);
    localStorage.setItem('mlwb-high-contrast', newValue.toString());
  };

  // Function to get current font size
  const getCurrentFontSize = () => {
    return fontSize;
  };

  // Function to check if font size is at minimum
  const isMinFontSize = () => {
    return fontSize <= FONT_SIZE_LIMITS.min;
  };

  // Function to check if font size is at maximum
  const isMaxFontSize = () => {
    return fontSize >= FONT_SIZE_LIMITS.max;
  };

  // Function to check if high contrast is enabled
  const getHighContrast = () => {
    return isHighContrast;
  };

  const value = {
    fontSize,
    isHighContrast,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
    toggleHighContrast,
    getCurrentFontSize,
    isMinFontSize,
    isMaxFontSize,
    getHighContrast,
    FONT_SIZE_LIMITS
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};

// Custom hook to use accessibility context
export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

export default AccessibilityContext;
